
import { useState } from 'react'
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Sector, Tooltip } from 'recharts'
import { formatMetricValue } from '../../lib/reportNew/formatNew'
import { formatPesoPorTipo } from '../../utils/format'
import { LEGEND_PROPS, TOOLTIP_PROPS, makeColorAt } from './chart-themeNew'

/** Sector activo: resalta la porción y muestra el porcentaje en el centro. */
function renderActiveShape(props) {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, percent, payload } = props
  return (
    <g>
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize={20} fontWeight={600} fill="#1d2b2e">
        {`${(percent * 100).toFixed(1)}%`}
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize={11.5} fill="#6c8083">
        {payload.name}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 5}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  )
}

/**
 * Tarta / donut sobre datos ya agregados.
 * `series` puede contener varias claves, pero solo se representa la primera:
 * una tarta solo admite una métrica.
 */
export function PieChartGraph({ data, series, format, donut = true, showLegend = true, chartType = 'donut' }) {
  const colorAt = makeColorAt(chartType)
  const [activeIndex, setActiveIndex] = useState(-1)

  // Multi-metric: each series becomes a separate pie slice
  const pieData = series.length > 1
    ? series.flatMap((metricName) =>
        data
          .map((item) => ({ name: metricName, value: Number(item[metricName] || 0) }))
          .filter((item) => item.value > 0)
      )
    : data
        .map((item) => ({ name: item.name, value: Number(item[series[0]] || 0) }))
        .filter((item) => item.value > 0)

  if (!pieData.length) {
    return (
      <div className="flex h-full items-center justify-center text-[13px] text-muted-foreground">
        No hay datos para representar
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
        <Pie
          data={pieData}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy={showLegend ? '44%' : '50%'}
          innerRadius={donut ? '52%' : 0}
          outerRadius={showLegend ? '74%' : '82%'}
          paddingAngle={donut ? 3 : 1}
          activeIndex={activeIndex >= 0 ? activeIndex : undefined}
          activeShape={renderActiveShape}
          onMouseEnter={(_, index) => setActiveIndex(index)}
          onMouseLeave={() => setActiveIndex(-1)}
          stroke="#fff"
          strokeWidth={2}
        >
          {pieData.map((entry, index) => (
            <Cell key={entry.name} fill={colorAt(index)} />
          ))}
        </Pie>
        <Tooltip
          {...TOOLTIP_PROPS}
          cursor={false}
          formatter={(value, name, item) => {
            const payload = item?.payload ?? item
            if (format.unit === 'kg' && payload && payload.peligroso !== undefined) {
              return [formatPesoPorTipo(value, Boolean(payload.peligroso)), name]
            }
            return [formatMetricValue(value, format), name]
          }}
        />
        {showLegend && (
          <Legend
            {...LEGEND_PROPS}
            verticalAlign="bottom"
            height={36}
            wrapperStyle={{ ...LEGEND_PROPS.wrapperStyle, lineHeight: '18px' }}
          />
        )}
      </PieChart>
    </ResponsiveContainer>
  )
}
